import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { RoleBadge } from "@/components/RoleBadge";
import { toast } from "sonner";
import { Send, Pin, X, Plus, Settings2 } from "lucide-react";

// Collective chat with leader-created rooms + pinned messages (F&F item 6,
// 2026-02). Extracted from CollectiveDetail.jsx when the single stream grew
// rooms. Pins are capped at 5 per room server-side, newest-first.

function NewRoomButton({ id, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    try {
      const room = await api.createCollectiveRoom(id, name.trim());
      setName("");
      setOpen(false);
      toast.success(`Room "${room.name}" created`);
      onCreated(room.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create room"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="chat-new-room-btn" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-dashed border-forest/40 text-forest hover:bg-forest/5 transition-colors shrink-0">
          <Plus className="w-3 h-3" /> Room
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="text-xs font-semibold text-forest mb-2">New chat room</div>
        <div className="flex gap-2">
          <Input data-testid="chat-new-room-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Planting Sundays" className="h-9 text-sm" maxLength={40} />
          <Button data-testid="chat-new-room-create" onClick={create} size="sm" className="rounded-full bg-forest hover:bg-forest-hover h-9">Add</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RoomManageButton({ id, room, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(room.name);
  useEffect(() => { setName(room.name); }, [room.name]);
  const save = async (patch) => {
    try {
      const updated = await api.updateCollectiveRoom(id, room.id, patch);
      setOpen(false);
      onChanged(updated);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update room"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="chat-room-manage-btn" title="Manage room" className="text-neutral-400 hover:text-forest transition-colors shrink-0">
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="text-xs font-semibold text-forest mb-2">Rename room</div>
        <div className="flex gap-2">
          <Input data-testid="chat-room-rename-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && save({ name: name.trim() })} className="h-9 text-sm" maxLength={40} />
          <Button data-testid="chat-room-rename-save" onClick={() => name.trim() && save({ name: name.trim() })} size="sm" className="rounded-full bg-forest hover:bg-forest-hover h-9">Save</Button>
        </div>
        {!room.is_default && (
          <button
            data-testid="chat-room-archive-btn"
            onClick={() => save({ archived: !room.archived })}
            className="mt-3 text-xs text-neutral-500 hover:text-red-600 underline underline-offset-2"
          >
            {room.archived ? "Unarchive this room" : "Archive this room"}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function CollectiveChat({ id, isLeader, onUnreadChange }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef();
  const lastMarkedRef = useRef(""); // "roomId|newestCreatedAt" — throttles seen-writes

  const reportUnread = (rs, activeId) => {
    onUnreadChange?.(rs.some((r) => r.unread && r.id !== activeId));
  };

  const loadRooms = (selectId) => api.collectiveRooms(id).then((rs) => {
    setRooms(rs);
    setRoomId((cur) => {
      let next = cur;
      if (selectId && rs.some((r) => r.id === selectId)) next = selectId;
      else if (!(cur && rs.some((r) => r.id === cur))) next = (rs.find((r) => r.is_default) || rs[0])?.id || null;
      reportUnread(rs, next);
      return next;
    });
  }).catch(() => {});
  useEffect(() => { loadRooms(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const loadMessages = () => { if (roomId) api.collectiveChat(id, roomId).then(setMessages).catch(() => {}); };
  useEffect(() => {
    loadMessages();
    const t = setInterval(() => { loadMessages(); loadRooms(); }, 12000); // rooms too — keeps unread dots live (GETs only)
    return () => clearInterval(t);
  }, [id, roomId]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs
  // Scroll only the message list, never the page (tab switch must not jump).
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Mark the active room seen — only on room open or when a NEWER message
  // arrives while viewing (idle polls never write).
  useEffect(() => {
    if (!roomId) return;
    const newest = messages.reduce((mx, m) => ((m.created_at || "") > mx ? m.created_at : mx), "");
    const key = `${roomId}|${newest}`;
    if (key === lastMarkedRef.current) return;
    lastMarkedRef.current = key;
    api.markRoomSeen(id, roomId).catch(() => {});
    setRooms((rs) => {
      const next = rs.map((r) => (r.id === roomId ? { ...r, unread: false } : r));
      reportUnread(next, roomId);
      return next;
    });
  }, [id, roomId, messages]); // eslint-disable-line react-hooks/exhaustive-deps -- reportUnread is render-scoped; effect keys on its real inputs

  const activeRoom = rooms.find((r) => r.id === roomId);
  const pinned = messages
    .filter((m) => m.pinned)
    .sort((a, b) => (b.pinned_at || "").localeCompare(a.pinned_at || ""));

  const send = async () => {
    if (!text.trim() || !roomId) return;
    try {
      const m = await api.sendCollectiveChat(id, text.trim(), roomId);
      setMessages((list) => [...list, m]);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };

  const togglePin = async (msgId) => {
    try {
      const res = await api.togglePinCollectiveMessage(id, msgId);
      toast.success(res.pinned ? "Pinned — members will see it at the top" : "Unpinned");
      loadMessages();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not pin"); }
  };

  return (
    <div data-testid="collective-chat" className="bg-white border border-neutral-200 rounded-xl p-5">
      {/* Room switcher */}
      <div className="flex items-center gap-2 flex-wrap pb-3 border-b border-neutral-100">
        {rooms.map((r) => (
          <button
            key={r.id}
            data-testid={`chat-room-${r.id}`}
            onClick={() => setRoomId(r.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors shrink-0 ${
              r.id === roomId
                ? "bg-forest text-white border-forest"
                : "border-neutral-200 text-neutral-600 hover:border-forest/40"
            } ${r.archived ? "opacity-50 italic" : ""}`}
          >
            {r.name}{r.archived ? " (archived)" : ""}
            {r.unread && r.id !== roomId && (
              <span data-testid={`room-unread-dot-${r.id}`} className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta ml-1.5 align-middle" />
            )}
          </button>
        ))}
        {isLeader && <NewRoomButton id={id} onCreated={(rid) => loadRooms(rid)} />}
        {isLeader && activeRoom && (
          <RoomManageButton
            id={id}
            room={activeRoom}
            onChanged={(updated) => {
              // archiving the active room bounces selection back to General
              const gen = rooms.find((r) => r.is_default);
              loadRooms(updated?.archived ? gen?.id : roomId);
            }}
          />
        )}
      </div>

      {/* Pinned strip */}
      {pinned.length > 0 && (
        <div data-testid="chat-pinned-strip" className="mt-3 rounded-lg bg-amber-50 border border-amber-200/70 p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-amber-700 inline-flex items-center gap-1.5">
            <Pin className="w-3 h-3" /> Pinned
          </div>
          {pinned.map((m) => (
            <div key={`pin-${m.id}`} data-testid={`pinned-msg-${m.id}`} className="flex items-start justify-between gap-2">
              <div className="text-sm text-neutral-700">
                <span className="font-semibold text-forest">{m.user_name}:</span> {m.content}
              </div>
              {isLeader && (
                <button data-testid={`unpin-msg-${m.id}`} onClick={() => togglePin(m.id)} title="Unpin"
                  className="text-amber-500 hover:text-amber-700 transition-colors shrink-0 mt-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={listRef} className="max-h-80 overflow-y-auto space-y-3 pr-1 mt-3">
        {messages.length === 0 && <div className="text-sm text-neutral-500">No messages yet — say hello!</div>}
        {messages.map((m) => (
          <div key={m.id} className="flex items-start gap-2.5 group">
            <Avatar className="w-7 h-7 shrink-0">
              <AvatarImage src={imgSrc(m.picture)} alt={m.user_name} />
              <AvatarFallback className="bg-forest text-white text-[10px]">{m.user_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-[11px] text-neutral-500">
                {m.user_name}{m.is_leader && <span className="text-terracotta font-medium"> · Leader</span>}
                {m.role_badge && <RoleBadge badge={m.role_badge} className="ml-1.5" />}
                {m.pinned && <Pin className="w-3 h-3 inline ml-1 text-amber-500" />}
              </div>
              <div className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-100 rounded-lg px-3 py-1.5 mt-0.5">{m.content}</div>
            </div>
            {isLeader && (
              <button
                data-testid={`pin-msg-${m.id}`}
                onClick={() => togglePin(m.id)}
                title={m.pinned ? "Unpin message" : "Pin message"}
                className={`shrink-0 mt-1 transition-all ${m.pinned ? "text-amber-500" : "text-neutral-300 opacity-0 group-hover:opacity-100 hover:text-amber-500"}`}
              >
                <Pin className="w-3.5 h-3.5" />
              </button>
            )}
            {user && m.user_id !== user.user_id && (
              <ReportButton targetType="collective_message" targetId={m.id} label=""
                className="shrink-0 mt-1 opacity-0 group-hover:opacity-100" />
            )}
          </div>
        ))}
      </div>

      {/* Composer */}
      {activeRoom?.archived ? (
        <div data-testid="chat-archived-notice" className="mt-3 text-xs text-neutral-500 italic">
          This room is archived — no new messages.
        </div>
      ) : (
        <div className="flex gap-2 mt-3">
          <Input data-testid="collective-chat-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={activeRoom ? `Message #${activeRoom.name}…` : "Message the collective…"} className="h-10" />
          <Button data-testid="collective-chat-send" onClick={send} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-4"><Send className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}
